import { Chip, Dialog, DialogActions, DialogContent, DialogTitle, Button, Stack, Typography, Box } from '@mui/material';
import { PERSONA_CAPABILITIES, SCOPE_ALL, type AgentSource, type PersonaDefinition } from '@diggens/types';

interface PersonaDefinitionDialogProps {
  definition: PersonaDefinition;
  source: AgentSource;
  version: number | null;
  onClose: () => void;
}

/** What a hired or app-shipped persona is made of: its brief, capabilities, scope and schedule, read-only. */
export function PersonaDefinitionDialog({ definition, source, version, onClose }: PersonaDefinitionDialogProps) {
  const types = (scope: string[]) => (scope.length === 0 ? 'nothing' : scope.map((type) => (type === SCOPE_ALL ? 'every type' : type)).join(', '));
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{definition.displayName}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
            <Chip label={source === 'hire' ? 'Hired' : `From app ${source.slice('app:'.length)}`} size="small" variant="outlined" />
            {version !== null && <Chip label={`Version ${version}`} size="small" variant="outlined" />}
            <Chip label={`${definition.model}, effort ${definition.effort}`} size="small" variant="outlined" />
            <Typography variant="caption" color="text.secondary">
              {definition.slug}
            </Typography>
          </Stack>
          <Typography variant="body2">{definition.description}</Typography>
          <Box>
            <Typography variant="subtitle2">Capabilities</Typography>
            <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2.5 }}>
              {definition.capabilities.map((capability) => (
                <Typography component="li" variant="body2" key={capability}>
                  <strong>{PERSONA_CAPABILITIES[capability].label}</strong>: {PERSONA_CAPABILITIES[capability].description}
                </Typography>
              ))}
            </Stack>
          </Box>
          <Box>
            <Typography variant="subtitle2">Scope</Typography>
            <Typography variant="body2">Reads {types(definition.scope.read)}.</Typography>
            <Typography variant="body2">Changes {types(definition.scope.write)}.</Typography>
          </Box>
          {definition.schedule && (
            <Box>
              <Typography variant="subtitle2">Schedule</Typography>
              <Typography variant="body2">
                {definition.schedule.cron} ({definition.schedule.timeZone ?? 'UTC'})
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                {definition.schedule.brief}
              </Typography>
            </Box>
          )}
          <Box>
            <Typography variant="subtitle2">Instructions</Typography>
            <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', m: 0 }}>
              {definition.instructions}
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
